// src/features/records/hooks/useThumbUrls.js

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import signedUrlService from '../services/signedUrlService';
import { pickThumbPaths } from '../utils/attachmentUtils';

/**
 * Hook to resolve thumbnail URLs from storage paths
 * Handles both pre-signed URLs and on-demand URL generation
 * 
 * @param {Object} attachment - Attachment object with type, thumbnails, and optional pre-signed URLs
 * @returns {Object} - { src, srcSet, loading, error, onImgErrorOnce }
 */
export function useThumbUrls(attachment) {
  // Check if parent already provided pre-signed URLs
  const presetPrimary = attachment.type === 'video' ? attachment.posterUrl : attachment.thumbnailUrl;

  // Determine which storage paths to use
  const { primaryPath, srcSetPaths } = useMemo(
    () => pickThumbPaths(attachment),
    [attachment]
  );

  const [state, setState] = useState({
    primaryUrl: presetPrimary || null,
    srcSet: null,
    loading: !presetPrimary && !!primaryPath,
    error: null,
    hasRetried: false,
  });

  // Cancellation & race guards to prevent memory leaks
  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Safe setState helper that checks if component is mounted and request is current
  const safeSetState = useCallback((updater, reqIdAtCall) => {
    // Allow both functional and object setState styles
    if (!mountedRef.current) return;
    // If a reqId was provided, ensure this update is for the latest request
    if (reqIdAtCall != null && reqIdAtCall !== reqIdRef.current) return;
    setState(updater);
  }, []);

  const buildFromResults = useCallback(
    (urlByPath) => {
      const primaryUrl = primaryPath
        ? urlByPath.get(primaryPath) || null
        : null;
      const srcSet =
        srcSetPaths
          .map(({ path, width }) => {
            const u = urlByPath.get(path);
            return u ? `${u} ${width}w` : null;
          })
          .filter(Boolean)
          .join(', ') || null;

      return { primaryUrl, srcSet };
    },
    [primaryPath, srcSetPaths]
  );

  const fetchUrls = useCallback(async () => {
    const myReqId = ++reqIdRef.current;

    // If we have a pre-signed URL, use it directly
    if (presetPrimary) {
      safeSetState(
        {
          primaryUrl: presetPrimary,
          srcSet: null,
          loading: false,
          error: null,
          hasRetried: false,
        },
        myReqId
      );
      return;
    }

    // If no primary path available, this is an error
    if (!primaryPath) {
      safeSetState(
        s => ({ 
          ...s, 
          loading: false, 
          error: 'No thumbnail paths available' 
        }),
        myReqId
      );
      return;
    }

    try {
      safeSetState(s => ({ ...s, loading: true, error: null }), myReqId);

      // Collect all unique paths we need to resolve
      const allPaths = [primaryPath, ...srcSetPaths.map(p => p.path)];
      const uniquePaths = Array.from(new Set(allPaths)).filter(Boolean);

      // Batch fetch signed URLs
      const results = await signedUrlService.getSignedUrls(uniquePaths);

      // Create URL map for easy lookup
      const urlByPath = new Map();
      for (const result of results) {
        if (result.url) {
          urlByPath.set(result.path, result.url);
        }
      }

      const { primaryUrl, srcSet } = buildFromResults(urlByPath);

      safeSetState(
        {
          primaryUrl,
          srcSet,
          loading: false,
          error: primaryUrl ? null : 'Failed to resolve thumbnail URL',
          hasRetried: false,
        },
        myReqId
      );
    } catch (err) {
      safeSetState(
        {
          primaryUrl: null,
          srcSet: null,
          loading: false,
          error: err?.message || 'Failed to fetch thumbnail URLs',
          hasRetried: false,
        },
        myReqId
      );
    }
  }, [presetPrimary, primaryPath, srcSetPaths, buildFromResults, safeSetState]);

  // Fetch URLs on mount or when dependencies change
  useEffect(() => {
    fetchUrls();
  }, [fetchUrls]);

  // Refresh **all** candidates once on error
  const onImgErrorOnce = useCallback(async () => {
    if (state.hasRetried) return;
    const myReqId = ++reqIdRef.current;
    
    try {
      const allPaths = [primaryPath, ...srcSetPaths.map(p => p.path)];
      const uniquePaths = Array.from(new Set(allPaths)).filter(Boolean);
      
      // Force refresh by using refreshUrl for each path to avoid cached expired URLs
      const refreshed = await Promise.all(
        uniquePaths.map(async (path) => {
          try {
            const url = await signedUrlService.refreshUrl(path);
            return { path, url };
          } catch (error) {
            console.error(`Failed to refresh URL for ${path}:`, error);
            return { path, url: null };
          }
        })
      );
      
      const urlByPath = new Map();
      for (const result of refreshed) {
        if (result.url) {
          urlByPath.set(result.path, result.url);
        }
      }

      const { primaryUrl, srcSet } = buildFromResults(urlByPath);

      safeSetState(
        s => ({
          ...s,
          primaryUrl,
          srcSet,
          hasRetried: true,
          error: primaryUrl ? null : 'Failed to refresh URLs',
        }),
        myReqId
      );
    } catch (err) {
      safeSetState(
        s => ({
          ...s,
          hasRetried: true,
          error: err?.message || 'Failed to refresh URLs',
        }),
        myReqId
      );
    }
  }, [state.hasRetried, primaryPath, srcSetPaths, buildFromResults, safeSetState]);

  return {
    src: state.primaryUrl || undefined,
    srcSet: state.srcSet || undefined,
    loading: state.loading,
    error: state.error,
    onImgErrorOnce,
  };
}